import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { Ticket } from '../ticket-boards/types'
import { objectify } from '@ezio/utils'
import { useEzioAPI } from '@ezio/services'
import { useTicketBoardsStore } from '../ticket-boards/store'

export const useTicketsStore = defineStore('tickets', () => {
    const { api } = useEzioAPI()

    const all = ref<{ [id: string]: Ticket }>({})

    const getByBoard = (boardId: string) => {
        return computed(() => {
            return Object.values(all.value).filter((t) => String(t.ticketBoardId) === String(boardId))
        })
    }

    const getByBoardGroupedByStatus = (boardId: string) => {
        return computed(() => {
            const boardsStore = useTicketBoardsStore()
            const tickets = Object.values(all.value).filter((t) => String(t.ticketBoardId) === String(boardId) && !t.archived)
            const grouped: { [statusId: string]: Ticket[] } = { unassigned: [] }

            for (const ticket of tickets) {
                const key = ticket.boardStatusId ? String(ticket.boardStatusId) : 'unassigned'
                if (!grouped[key]) {
                    grouped[key] = []
                }
                grouped[key].push(ticket)
            }

            // Sort tickets by board ticketOrder
            const ticketOrder = boardsStore.getTicketOrder(boardId)
            for (const key in grouped) {
                const order = ticketOrder[key] || []
                grouped[key].sort((a, b) => {
                    const idxA = order.indexOf(String(a.id))
                    const idxB = order.indexOf(String(b.id))
                    if (idxA === -1 && idxB === -1) return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                    if (idxA === -1) return 1
                    if (idxB === -1) return -1
                    return idxA - idxB
                })
            }

            return grouped
        })
    }

    const fetchAll = async () => {
        try {
            const response = await api.get('tickets', {
                pagination: false,
                include: 'created_by,ticket_assignments,ticket_objects,ticket_comments,ticket_comments.user'
            })
            store(response.data)
            return response.data as Ticket[]
        } catch (error) {
            return []
        }
    }

    const fetchMyTickets = async (userId: string) => {
        try {
            const response = await api.get('tickets', {
                assigned_to_user_id: userId,
                archived: 'all',
                pagination: false,
                include: 'created_by,ticket_assignments,ticket_objects,ticket_comments,ticket_comments.user'
            })
            store(response.data)
            return response.data as Ticket[]
        } catch (error) {
            return []
        }
    }

    const fetchCreatedByMe = async (userId: string) => {
        try {
            const response = await api.get('tickets', {
                created_by_id: userId,
                archived: 'all',
                pagination: false,
                include: 'created_by,ticket_assignments,ticket_objects,ticket_comments,ticket_comments.user'
            })
            store(response.data)
            return response.data as Ticket[]
        } catch (error) {
            return []
        }
    }

    const fetchByObject = async (objectType: string, objectId: string) => {
        try {
            const response = await api.get('tickets', {
                object_type: objectType,
                object_id: objectId,
                archived: 'all',
                pagination: false,
                include: 'created_by,ticket_assignments,ticket_objects,ticket_comments,ticket_comments.user'
            })
            store(response.data)
            return response.data as Ticket[]
        } catch (error) {
            return []
        }
    }

    const fetchByBoard = async (boardId: string) => {
        try {
            const response = await api.get('tickets', {
                ticket_board_id: boardId,
                pagination: false,
                include: 'created_by,ticket_assignments,ticket_objects,ticket_comments,ticket_comments.user'
            })
            store(response.data)
        } catch (error) {
            //
        }
    }

    const fetch = async (id: string) => {
        const response = await api.get('tickets/' + id, {
            include: 'created_by,ticket_assignments,ticket_objects,ticket_comments,ticket_comments.user'
        })
        store([response.data])
        return response.data
    }

    const create = async (data: { title: string; description?: string; ticket_board_id: string; board_status_id?: string }) => {
        const response = await api.post('tickets', {
            ...data,
            include: 'created_by,ticket_assignments,ticket_objects,ticket_comments,ticket_comments.user'
        })
        store([response.data])
        return response.data
    }

    const update = async (id: string, data: any) => {
        const response = await api.put('tickets/' + id, {
            ...data,
            include: 'created_by,ticket_assignments,ticket_objects,ticket_comments,ticket_comments.user'
        })
        store([response.data])
        return response.data
    }

    const move = async (ticketId: string, boardStatusId: string | null) => {
        // Optimistic update
        const previous = all.value[ticketId]
        if (previous) {
            all.value[ticketId] = { ...previous, boardStatusId }
        }

        try {
            const response = await api.put('tickets/' + ticketId, {
                board_status_id: boardStatusId,
                include: 'created_by,ticket_assignments,ticket_objects,ticket_comments,ticket_comments.user'
            })
            store([response.data])
            return response.data
        } catch (error) {
            // Rollback
            if (previous) {
                all.value[ticketId] = previous
            }
            throw error
        }
    }

    const addAttachment = async (ticketId: string, data: string) => {
        const response = await api.post('tickets/' + ticketId + '/attachments', {
            attachment: { data },
            include: 'created_by,ticket_assignments,ticket_objects,ticket_comments,ticket_comments.user'
        })
        store([response.data])
    }

    const removeAttachment = async (ticketId: string, attachmentId: string) => {
        const response = await api.delete('tickets/' + ticketId + '/attachments', {
            attachment_id: attachmentId,
            include: 'created_by,ticket_assignments,ticket_objects,ticket_comments,ticket_comments.user'
        })
        store([response.data])
    }

    const remove = async (id: string) => {
        await api.delete('tickets/' + id)
        const temp = { ...all.value }
        delete temp[id]
        all.value = temp
    }

    const store = (tickets: Ticket[]) => {
        const items = tickets.map((t) => Object.assign({}, all.value[t.id], t))
        all.value = Object.assign({}, all.value, objectify(items))
    }

    const reset = () => {
        all.value = {}
    }

    return {
        all,
        getByBoard,
        getByBoardGroupedByStatus,
        fetchAll,
        fetchMyTickets,
        fetchCreatedByMe,
        fetchByObject,
        fetchByBoard,
        fetch,
        create,
        update,
        move,
        addAttachment,
        removeAttachment,
        remove,
        reset,
        store
    }
})
